import React from "react";
import { CreditCard, UserCircle, Users } from "lucide-react";
import { BillingBanner } from "../../../components/billing/BillingBanner";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import { ButtonLink } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ThemeToggle } from "../../../components/theme/ThemeToggle";
import type { MainAppPageProps } from "../types";
import { OrganizationSettingsForm } from "./OrganizationSettingsForm";
import { DeleteAccountSection } from "./DeleteAccountSection";

type SettingsPageProps = Pick<
  MainAppPageProps,
  "csrfToken" | "routes" | "organization" | "tradeOptions" | "billing"
> & {
  hasBusiness?: boolean;
  canAccessBackend?: boolean;
};

export const SettingsPage: React.FC<SettingsPageProps> = ({
  csrfToken,
  routes,
  organization,
  tradeOptions = [],
  billing,
  hasBusiness = false,
  canAccessBackend = false,
}) => {
  const teamPath = routes.organizationMembers ?? "/organization/members";

  return (
    <DashboardPageLayout title="Settings">
      {canAccessBackend ? (
        <BillingBanner billing={billing} billingRoute={routes.billing} />
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="text-base">Look</CardTitle>
            <CardDescription>
              Light or dark for this app. It does not change your public
              website.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ThemeToggle />
          </CardContent>
        </Card>

        <Card className="bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserCircle className="h-4 w-4 shrink-0" />
              Account
            </CardTitle>
            <CardDescription>Your name and profile photo.</CardDescription>
          </CardHeader>
          <CardContent>
            <ButtonLink to={routes.account} variant="outline">
              Open account
            </ButtonLink>
          </CardContent>
        </Card>

        {canAccessBackend && organization ? (
          <Card className="bg-card md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Company</CardTitle>
              <CardDescription>
                Name, phone, and trade used across ToolKit and your website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OrganizationSettingsForm
                csrfToken={csrfToken}
                routes={routes}
                organization={organization}
                tradeOptions={tradeOptions}
              />
            </CardContent>
          </Card>
        ) : null}

        {canAccessBackend ? (
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4 shrink-0" />
                Team
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ButtonLink to={teamPath} variant="outline">
                Invite my crew
              </ButtonLink>
            </CardContent>
          </Card>
        ) : null}

        {canAccessBackend && hasBusiness ? (
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 shrink-0" />
                Billing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ButtonLink to={routes.billing} variant="outline">
                Open billing
              </ButtonLink>
            </CardContent>
          </Card>
        ) : null}

        <Card className="bg-card md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Delete account</CardTitle>
            <CardDescription>
              This is irreversible. You will have to create a new account if you
              want to use ToolKit again. Job sites, photos, and other company
              content you added will stay with the company.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DeleteAccountSection csrfToken={csrfToken} routes={routes} />
          </CardContent>
        </Card>
      </div>
    </DashboardPageLayout>
  );
};
